import React from 'react'
import ClosingActionBand from './ClosingActionBand'

const AUDIO_CLOSING_CONTENT = {
  '/sound-system-rental': {
    heading: 'Ready to plan the right PA system?',
    description: 'Get a complete-system recommendation or compare packaged sound-system options and pricing.',
    proofItems: ['Documented South Florida event projects', 'Verified sound-related Google feedback', 'Complete-system planning guidance']
  },
  '/speaker-rental': {
    heading: 'Ready to reserve the right speakers?',
    description: 'Compare individual speakers and pricing online, or ask an audio specialist to help with coverage, delivery, and setup.',
    proofItems: ['Current speaker catalog pricing', 'Documented event work', 'Verified sound-related Google feedback']
  },
  '/orlando/speaker-rental': {
    heading: 'Ready to reserve speakers in Central Florida?',
    description: 'Compare Orlando speaker inventory online, or ask an audio specialist about coverage, Tampa delivery, setup, and short-notice availability.',
    proofItems: ['Orlando inventory shown first', 'Fulfillment pricing before checkout', 'Verified sound-related Google feedback']
  },
  '/microphone-rental': {
    heading: 'Ready to choose your microphones?',
    description: 'Browse handheld, lapel, headset, and wireless options, or ask for help matching the microphones to your event.',
    proofItems: ['Current microphone catalog pricing', 'Microphone-relevant customer feedback', 'Pickup or delivery selected before checkout']
  },
  '/karaoke-rental': {
    heading: 'Ready to choose your karaoke package?',
    description: 'Compare ready-to-use packages and pricing, or ask for help matching microphones, speakers, and screens to your party.',
    proofItems: ['Ready-to-use package details', 'Party and private-event feedback', 'Pickup or delivery selected before checkout']
  },
  '/orlando/karaoke-rental': {
    heading: 'Ready to choose an Orlando karaoke package?',
    description: 'Compare complete local packages online, or ask the Orlando team to match microphones, speaker coverage, a lyrics display, and fulfillment to your event.',
    proofItems: ['Orlando inventory shown first', 'Exact package contents before booking', 'Pickup or delivery selected after dates']
  },
  '/sound/microphone-and-speaker': {
    heading: 'Ready to choose a microphone and speaker bundle?',
    description: 'Compare preconfigured bundles online, or ask for a recommendation based on audience size, venue, and presentation needs.',
    proofItems: ['Bundle contents and pricing shown upfront', 'South Florida service guidance', 'Verified Google feedback']
  },
  '/sound/array': {
    heading: 'Ready to plan line-array coverage?',
    description: 'Ask for a line-array recommendation or compare the available systems for your audience, venue, and program.',
    proofItems: ['Line-array-specific selection guidance', 'South Florida event service context', 'Verified Google feedback']
  },
  '/band-equipment/mixers': {
    heading: 'Ready to choose a live sound mixer?',
    description: 'Compare live sound mixers online, or ask an audio specialist to match inputs, outputs, routing, and operator needs.',
    proofItems: ['Live-sound-specific mixer guidance', 'South Florida event service context', 'Verified Google feedback']
  },
  '/sound/amplifiers': {
    heading: 'Ready to choose the right amplifier or amp rack?',
    description: 'Ask for a compatibility recommendation or review the available PA power amplifiers and amp racks.',
    proofItems: ['PA amplifier and amp-rack terminology', 'South Florida event service context', 'Verified Google feedback']
  },
  '/sound/monitors': {
    heading: 'Ready to choose your stage monitors?',
    description: 'Compare stage-monitor options online, or ask for help matching wedges or in-ear monitoring to the performers and stage.',
    proofItems: ['Stage-monitor and in-ear-monitor focus', 'South Florida event service context', 'Verified Google feedback']
  },
  '/sound/recorders': {
    heading: 'Ready to choose an event recorder?',
    description: 'Review the current recorder option, or ask for help matching portable or field recording to the event workflow.',
    proofItems: ['Portable and field-recorder terminology', 'South Florida event service context', 'Verified Google feedback']
  },
  '/sound/subwoofer': {
    heading: 'Ready to add the right low-frequency support?',
    description: 'Compare subwoofers online, or ask for help matching cabinet size and quantity to the room, audience, and program.',
    proofItems: ['Subwoofer-specific sizing guidance', 'South Florida event service context', 'Verified Google feedback']
  }
}

const toHeadingId = pagePath => `${String(pagePath || 'audio')
  .replace(/^\//, '')
  .replace(/[^a-z0-9]+/gi, '-')}-closing-action-heading`

const AudioClosingActionBand = ({ pagePath, primaryAction, secondaryAction }) => {
  const content = AUDIO_CLOSING_CONTENT[pagePath]
  if (!content || !primaryAction?.label || !secondaryAction?.label) return null

  return (
    <ClosingActionBand
      ariaLabel={`${content.heading} options`}
      description={content.description}
      eyebrow='Choose your next step with confidence'
      heading={content.heading}
      headingId={toHeadingId(pagePath)}
      primaryAction={primaryAction}
      proofItems={content.proofItems}
      secondaryAction={secondaryAction}
    />
  )
}

export default AudioClosingActionBand
